import { z } from "zod";
import { DECIMAL_ZERO, DECIMAL_CONFIG, decimalSchema, positiveDecimalSchema, nonnegativeDecimalSchema } from "@shared/values/decimal";
import { initialStockIssues, INITIAL_STOCK_TYPE_VALUES } from "@shared/inventory/chemical-initial-stock";
import { transactionTypeSchema } from "@shared/inventory/transaction-types";
import { contactTypeSchema, CONTACT_COUNTRY_MAX_LENGTH } from "@shared/platform/schema";
import { isValidBatchOrProductNumberChars } from "@shared/inventory/batch-number";
import {
  TRANSACTION_AMEND_FIELDS,
  TRANSACTION_FIELD_POLICY,
  TransactionAmendField,
  TransactionAmendSchemaKind,
} from "@shared/inventory/transaction-field-policy";
import { arrivedDateLabel, ORDER_DIRECTION_VALUES, orderedAtLabel, submittedAtLabel } from "@/lib/order-display";
import { lifecycleDateIssues, type LifecycleDateLabels } from "@shared/orders/order-lifecycle-policy";
import { FORM_SCHEMAS_COPY, FORM_VALIDATION_MESSAGES } from "@/lib/ui-labels";
import { deliveryRowFormSchema, deliveryScheduleFormIssues, type DeliveryRow } from "@/lib/delivery-schedule-form";
import type { OrderDirection } from "@/lib/order-display";

function toLifecycleDate(value: string): Date | null {
  return value && value.trim() ? parseDateOnly(value) : null;
}

function lifecycleDateLabels(direction: OrderDirection): LifecycleDateLabels {
  return {
    submittedAt: submittedAtLabel(direction),
    orderedAt: orderedAtLabel(direction),
    arrivedDate: arrivedDateLabel(direction),
  };
}

function addDeliveryScheduleIssues(ctx: z.RefinementCtx, rows: DeliveryRow[], submittedAt: Date | null, direction: OrderDirection) {
  for (const issue of deliveryScheduleFormIssues(rows, submittedAt, lifecycleDateLabels(direction))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: issue.path });
  }
}
import { ORDER_DIRECTIONS, ORDER_STATUS_VALUES } from "@shared/orders/order-types";
import { parseDateOnly } from "@shared/values/date-only";

function stripPriceCommas(val: string) {
  return val === "" ? val : val.replace(/,/g, "");
}

function validMoneyText(value: string): boolean {
  return value === "" || decimalSchema.safeParse(value).success;
}

const moneyTextSchema = z.string().transform(stripPriceCommas).refine(validMoneyText, DECIMAL_CONFIG.invalid);
const optionalMoneyTextSchema = moneyTextSchema.optional().transform(value => value === "" ? null : value);

const BATCH_OR_PRODUCT_NUMBER_ERROR = FORM_SCHEMAS_COPY.onlyLettersNumbersAndDashesAre;

const casNumberSchema = z.string().min(1, FORM_SCHEMAS_COPY.casNumberIsRequired);

export const loginSchema = z.object({
  email: z.string().email(FORM_SCHEMAS_COPY.invalidEmailAddress),
  password: z.string().min(1, FORM_SCHEMAS_COPY.passwordIsRequired),
});
export type LoginData = z.infer<typeof loginSchema>;

export const contactFormSchema = z.object({
  company: z.string().min(1, FORM_SCHEMAS_COPY.companyNameIsRequired),
  contactType: contactTypeSchema,
  address: z.string().optional(),
  country: z.string().max(CONTACT_COUNTRY_MAX_LENGTH).optional(),
  notes: z.string().optional(),
});
export type ContactFormValues = z.infer<typeof contactFormSchema>;

export const contactPersonFormSchema = z.object({
  name: z.string().min(1, FORM_SCHEMAS_COPY.nameIsRequired),
  email: z.string().email(FORM_SCHEMAS_COPY.invalidEmailFormat).optional().or(z.literal("")),
  phone: z.string().optional(),
  role: z.string().optional(),
});
export type ContactPersonFormValues = z.infer<typeof contactPersonFormSchema>;

export const transactionFormSchema = z.object({
  chemicalId: z.coerce.number().positive(FORM_SCHEMAS_COPY.chemicalIsRequired),
  transactionType: transactionTypeSchema,
  inventoryApplied: z.boolean().default(true),
  quantity: decimalSchema.optional(),
  finalCountedAmount: decimalSchema.optional(),
  unit: z.string().min(1, FORM_SCHEMAS_COPY.unitIsRequired),
  supplierId: z.coerce.number().optional(),
  supplierContactId: z.coerce.number().optional(),
  purchaserId: z.coerce.number().optional(),
  purchaserContactId: z.coerce.number().optional(),
  price: optionalMoneyTextSchema,
  referenceNumber: z.string().optional(),
  supplierBatchNumber: z.string().optional(),
  usageDetails: z.string().optional(),
  disposalReason: z.string().optional(),
  notes: z.string().optional(),
  transactionDate: z.string().optional().transform(val => val === "" ? undefined : val),
}).superRefine((data, ctx) => {
  if (data.transactionType === "CORRECTION") {
    if (!nonnegativeDecimalSchema.safeParse(data.finalCountedAmount).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: FORM_SCHEMAS_COPY.finalCountedAmountIsRequired,
        path: ["finalCountedAmount"],
      });
    }
    return;
  }

  if (!positiveDecimalSchema.safeParse(data.quantity).success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: FORM_VALIDATION_MESSAGES.quantityPositive,
      path: ["quantity"],
    });
  }

});
export type TransactionFormValues = z.infer<typeof transactionFormSchema>;

const amendSchemaByKind = {
  contactId: () => z.coerce.number().optional(),
  string: () => z.string().optional(),
  moneyString: () => optionalMoneyTextSchema,
  dateTimeString: () => z.string().optional(),
} satisfies Record<TransactionAmendSchemaKind, () => z.ZodTypeAny>;

export function buildTransactionAmendSchemaShape() {
  const shape = {} as Record<TransactionAmendField, z.ZodTypeAny>;
  for (const field of TRANSACTION_AMEND_FIELDS) {
    shape[field] = amendSchemaByKind[TRANSACTION_FIELD_POLICY[field].schemaKind]();
  }
  return shape;
}

export const transactionAmendSchema = z.object(buildTransactionAmendSchemaShape())
  .extend({ transactionType: z.string().optional() });
export type TransactionAmendValues = z.infer<typeof transactionAmendSchema>;

export const batchSplitSchema = z.object({
  children: z.array(z.object({
    batchNumber: z.string().optional(),
    quantity: positiveDecimalSchema,
  })).min(1, FORM_SCHEMAS_COPY.atLeastOneChildBatchIs),
});
export type BatchSplitFormValues = z.infer<typeof batchSplitSchema>;

export const chemicalFormSchema = z.object({
  productNumber: z.string().min(1, FORM_SCHEMAS_COPY.productNumberIsRequired)
    .transform(val => val.toUpperCase())
    .refine(val => isValidBatchOrProductNumberChars(val), BATCH_OR_PRODUCT_NUMBER_ERROR),
  name: z.string().min(1, FORM_SCHEMAS_COPY.chemicalNameIsRequired),
  casNumber: casNumberSchema,
  batchNumber: z.string().trim()
    .transform(val => val === "" ? null : val.toUpperCase())
    .refine(val => val === null || isValidBatchOrProductNumberChars(val), BATCH_OR_PRODUCT_NUMBER_ERROR),
  location: z.string().min(1, FORM_SCHEMAS_COPY.locationIsRequired),
  unit: z.string().min(1, FORM_SCHEMAS_COPY.unitIsRequired),
  initialAmount: nonnegativeDecimalSchema.default(DECIMAL_ZERO),
  initialTransactionType: z.enum(INITIAL_STOCK_TYPE_VALUES).optional(),
  initialTransactionDate: z.string().optional(),
  openingBalance: z.object({
    supplierContactId: z.coerce.number().optional(),
    price: optionalMoneyTextSchema,
    referenceNumber: z.string().optional(),
    supplierBatchNumber: z.string().optional(),
  }).optional(),
}).superRefine((data, ctx) => {
  for (const issue of initialStockIssues(data)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.field.split("."), message: issue.message });
  }
});
export type ChemicalFormData = z.infer<typeof chemicalFormSchema>;

export const orderFormSchema = z.object({
  direction: z.enum(ORDER_DIRECTION_VALUES),
  contactId: z.string(),
  chemicalName: z.string(),
  casNumber: z.string(),
  unit: z.string(),
  pricePerItem: moneyTextSchema,
  internalProductNumber: z.string()
    .transform(val => val.toUpperCase())
    .refine(val => isValidBatchOrProductNumberChars(val), BATCH_OR_PRODUCT_NUMBER_ERROR),
  externalProductNumber: z.string(),
  referenceNumber: z.string(),
  customerPoNumber: z.string().default(""),
  contactAddressId: z.string().default(""),
  deliverySchedule: z.array(deliveryRowFormSchema),
  requestedById: z.string(),
  customerRequestorId: z.string(),
  notes: z.string(),
  status: z.enum(ORDER_STATUS_VALUES),
  submittedAt: z.string().min(1, FORM_SCHEMAS_COPY.requestedDateIsRequired),
  orderedAt: z.string(),
  arrivedDate: z.string(),
  paidAmount: moneyTextSchema,
  paidDate: z.string(),
}).superRefine((data, ctx) => {
  if (data.direction === ORDER_DIRECTIONS.purchase && !data.requestedById.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: FORM_SCHEMAS_COPY.requestedByIsRequired, path: ["requestedById"] });
  }
  if (data.direction === ORDER_DIRECTIONS.sale && !data.customerRequestorId.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: FORM_SCHEMAS_COPY.customerRequestorIsRequired, path: ["customerRequestorId"] });
  }
  if (!data.chemicalName.trim() && !data.casNumber.trim()) {
    const message = FORM_SCHEMAS_COPY.enterAChemicalNameOrCas;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["chemicalName"] });
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["casNumber"] });
  }
  addDeliveryScheduleIssues(ctx, data.deliverySchedule, toLifecycleDate(data.submittedAt), data.direction);
  for (const issue of lifecycleDateIssues(
    {
      status: data.status,
      submittedAt: toLifecycleDate(data.submittedAt),
      orderedAt: toLifecycleDate(data.orderedAt),
      arrivedDate: toLifecycleDate(data.arrivedDate),
    },
    new Date(),
    lifecycleDateLabels(data.direction),
  )) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message, path: [issue.field] });
  }
});
export type OrderFormSchemaValues = z.infer<typeof orderFormSchema>;
export type OrderFormSubmissionValues = OrderFormSchemaValues & {
  contactName: string;
  requestedBy: string | null;
};

export const orderPaymentFormSchema = z.object({
  paidAmount: moneyTextSchema,
  paidDate: z.string(),
  invoiceNumber: z.string(),
  invoiceDate: z.string(),
  notes: z.string(),
});
export type OrderPaymentFormValues = z.infer<typeof orderPaymentFormSchema>;

export const orderCreditMemoFormSchema = z.object({
  amount: moneyTextSchema,
  date: z.string(),
  referenceNumber: z.string(),
  notes: z.string(),
});
export type OrderCreditMemoFormValues = z.infer<typeof orderCreditMemoFormSchema>;

const orderAmendFields = z.object({
  direction: z.enum(ORDER_DIRECTION_VALUES),
  contactId: z.string().min(1, FORM_SCHEMAS_COPY.contactIsRequired),
  chemicalName: z.string(),
  casNumber: z.string(),
  unit: z.string().min(1, FORM_SCHEMAS_COPY.unitIsRequired),
  pricePerItem: moneyTextSchema,
  internalProductNumber: z.string()
    .transform(val => val.toUpperCase())
    .refine(val => isValidBatchOrProductNumberChars(val), BATCH_OR_PRODUCT_NUMBER_ERROR),
  externalProductNumber: z.string(),
  referenceNumber: z.string(),
  customerPoNumber: z.string().default(""),
  contactAddressId: z.string().default(""),
  requestedById: z.string(),
  customerRequestorId: z.string(),
  deliverySchedule: z.array(deliveryRowFormSchema),
  notes: z.string(),
});

export const ORDER_FORM_FIELDS = orderAmendFields.keyof().enum;

export const orderAmendFormSchema = orderAmendFields.superRefine((data, ctx) => {
  if (!data.chemicalName.trim() && !data.casNumber.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: FORM_SCHEMAS_COPY.chemicalNameOrCasNumberIs, path: ["chemicalName"] });
  }
  const requestor = data.direction === ORDER_DIRECTIONS.purchase ? data.requestedById : data.customerRequestorId;
  if (!requestor) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: FORM_SCHEMAS_COPY.requestorIsRequired,
      path: [data.direction === ORDER_DIRECTIONS.purchase ? "requestedById" : "customerRequestorId"],
    });
  }
});
export function orderAmendFormSchemaForRequest(submittedAt: Date | null) {
  return orderAmendFormSchema.superRefine((data, ctx) => {
    addDeliveryScheduleIssues(ctx, data.deliverySchedule, submittedAt, data.direction);
  });
}

export type OrderAmendFormValues = z.infer<typeof orderAmendFormSchema>;
export type OrderAmendSubmissionValues = OrderAmendFormValues & {
  contactName: string;
  requestedBy: string | null;
};
